import { useParams, useNavigate, useLocation } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { closeAllModals, modals } from '@mantine/modals';
import { useDisclosure } from '@mantine/hooks';
import SetupSpecifyParkingLotsModal from '../components/modal/parking-contract/SetupSpecifyParkingLotsModal.tsx';
import {
  Stack,
  Paper,
  Card,
  Box,
  Group,
  Text,
  Title,
  Button,
  SimpleGrid,
  Input,
  Alert,
  LoadingOverlay,
  Divider,
} from '@mantine/core';
import {
  IconEdit,
  IconInfoCircle,
  IconStackPop,
  IconTrash,
  IconArrowRight,
} from '@tabler/icons-react';
import { DataTable, DataTableColumn } from 'mantine-datatable';

import usePartner from '../hooks/partners/usePartner.ts';
import useParkingContract from '../hooks/parking-contracts/use-parking-contract.ts';
import useDownloadParkingContractAttachment from '../hooks/parking-contracts/use-download-parking-contract-attachment.ts';
import useUpdateParkingContract from '../hooks/parking-contracts/use-update-parking-contract.ts';
import usePublishParkingContract from '../hooks/parking-contracts/use-publish-parking-contract.ts';
import useBriefParkingLots from '../hooks/partners/useBriefParkingLots.ts';
import useSetupParkingLots from '../hooks/parking-contracts/use-setup-parking-lots.ts';
import useDeleteParkingContract from '../hooks/parking-contracts/use-delete-parking-contract.ts';
import { formatDate } from '../utils/DateUtil.ts';

import type { Attachment } from '../models/attachment.ts';
import type {
  MutateParkingContractFormValues,
  MutateParkingContractFormInitialValues,
} from '../components/ui/MutateParkingContractForm.tsx';
import type {
  BaseUpdateParkingContractRequest,
  UpdateDefaultParkingContractRequest,
  UpdateSpecifyParkingContractRequest,
  UploadParkingContractAttachmentRequest,
} from '../api/requests/parking-contract-request.ts';
import type {
  DefaultConfigType,
  BankDiscountConfigType,
  PbaReferralConfigType,
  ParkingLotFunctionType,
} from '../models/parking-contract.ts';
import type { BriefParkingLot } from '../models/parking-contract.ts';

import { executeDownloadAttachment } from '../utils/attachment-utils.ts';
import MutateParkingContractForm from '../components/ui/MutateParkingContractForm.tsx';
import PreviousPageButton from '../components/ui/PreviousPageButton.tsx';
import {
  PROFILE_TYPE_LABEL,
  PARKING_CONTRACT_DISBURSEMENT_FEE_PROCESSING_METHOD_LABEL,
  PARKING_CONTRACT_REFUND_FEE_PROCESSING_METHOD_LABEL,
  PARKING_CONTRACT_BILLING_CYCLE_LABEL,
  PARKING_CONTRACT_PARKING_LOT_FUNCTION_TYPE_LABEL,
  defaultProfitSharingText,
  bankDiscountProfitSharingText,
  pbaReferralProfitSharingText,
} from '../const/labels.ts';
import { notifySuccess } from '../utils/NotifySuccess.tsx';
import { notifyError } from '../utils/NotifyError.tsx';

export default function ParkingContractEditPage() {
  // hooks
  const navigate = useNavigate();
  const { state } = useLocation();
  const { partnerId, id: id } = useParams();

  const queryClient = useQueryClient();
  const { partner, isLoading: partnerIsLoading } = usePartner(partnerId);
  const { parkingContract, isLoading: isParkingContractLoading } =
    useParkingContract(partnerId, id);
  const { briefParkingLots, isLoading: isBriefParkingLotsLoading } =
    useBriefParkingLots(partnerId);
  const {
    mutate: downloadParkingContractAttachment,
    isPending: isDownloadParkingContractAttachmentPending,
  } = useDownloadParkingContractAttachment();
  const { mutate: updateContract, isPending: isUpdateContractPending } =
    useUpdateParkingContract();
  const { mutate: publishContract } = usePublishParkingContract();
  const { mutate: setupParingLots, isPending: isSetupParkingLotsPending } =
    useSetupParkingLots();
  const { mutate: deleteContract } = useDeleteParkingContract();

  const [isCreatedNoticeClosed, setCreatedNoticeClosed] = useState(false);
  const [isWarningClosed, setWarningClosed] = useState(false);
  const [isEditMode, setEditMode] = useState(false);

  const [
    setupParkingLotsModalOpened,
    { open: openSetupParkingLotsModal, close: closeSetupParkingLotsModal },
  ] = useDisclosure(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (
    partnerIsLoading ||
    isParkingContractLoading ||
    isBriefParkingLotsLoading
  ) {
    return (
      <LoadingOverlay visible loaderProps={{ color: 'cyan', type: 'bars' }} />
    );
  }

  // 不是 DRAFT 階段的合約都不可編輯
  if (parkingContract && parkingContract.state !== 'DRAFT') {
    navigate(`/partners/${partnerId}/parking-contracts/${id}`);
  }

  // constants
  const isAttachmentsValid: boolean = parkingContract.attachments.length === 1;
  const isSpecifyParkingLotValid: boolean = !(
    parkingContract.profileType === 'SPECIFY' &&
    parkingContract.specifyParkingLots.length < 1
  );
  const shouldDisplayCreatedNotice: boolean =
    !!state?.created && !isCreatedNoticeClosed;
  const shouldDisplayWarning: boolean =
    (!isAttachmentsValid || !isSpecifyParkingLotValid) && !isWarningClosed;
  const isPublishable: boolean =
    !isEditMode && isAttachmentsValid && isSpecifyParkingLotValid;

  const initialFormValues = (): MutateParkingContractFormInitialValues => {
    const alprProfitSharingConfig = parkingContract.profitSharingConfigs.ALPR;
    const kioskProfitSharingConfig = parkingContract.profitSharingConfigs.KIOSK;
    const payTaipeiProfitSharingConfig =
      parkingContract.profitSharingConfigs.PAY_TAIPEI;
    return {
      name: parkingContract.name,
      profileType: parkingContract.profileType,
      paperContractStartAt: parkingContract.paperContractStartAt,
      paperContractEndAt: parkingContract.paperContractEndAt,
      activeStartAt: parkingContract.activeStartAt,
      activeEndAt: parkingContract.activeEndAt,
      disbursementFeeProcessingMethod:
        parkingContract.disbursementFeeProcessingMethod,
      refundFeeProcessingMethod: parkingContract.refundFeeProcessingMethod,
      billingCycle: parkingContract.billingCycle,
      parkingLotFunctionTypes: parkingContract.parkingLotFunctionTypes,
      profitSharingConfigs: {
        ALPR: {
          parkingLotFunctionType:
            alprProfitSharingConfig.parkingLotFunctionType,
          allowDisbursementAmountOverLimit:
            alprProfitSharingConfig.allowDisbursementAmountOverLimit,
          defaultConfig: {
            type: alprProfitSharingConfig.defaultConfig
              .type as DefaultConfigType,
            value: alprProfitSharingConfig.defaultConfig.value || '0',
          },
          bankDiscountConfig: {
            type: alprProfitSharingConfig.bankDiscountConfig
              .type as BankDiscountConfigType,
            value: alprProfitSharingConfig.bankDiscountConfig.value,
          },
          pbaReferralConfig: {
            type: alprProfitSharingConfig.pbaReferralConfig
              .type as PbaReferralConfigType,
            value: alprProfitSharingConfig.pbaReferralConfig.value,
          },
        },
        KIOSK: {
          parkingLotFunctionType:
            kioskProfitSharingConfig.parkingLotFunctionType,
          allowDisbursementAmountOverLimit:
            kioskProfitSharingConfig.allowDisbursementAmountOverLimit,
          defaultConfig: {
            type: kioskProfitSharingConfig.defaultConfig
              .type as DefaultConfigType,
            value: kioskProfitSharingConfig.defaultConfig.value,
          },
          bankDiscountConfig: {
            type: kioskProfitSharingConfig.bankDiscountConfig
              .type as BankDiscountConfigType,
            value: kioskProfitSharingConfig.bankDiscountConfig.value,
          },
          pbaReferralConfig: {
            type: kioskProfitSharingConfig.pbaReferralConfig
              .type as PbaReferralConfigType,
            value: kioskProfitSharingConfig.pbaReferralConfig.value,
          },
        },
        PAY_TAIPEI: {
          parkingLotFunctionType:
            payTaipeiProfitSharingConfig.parkingLotFunctionType,
          allowDisbursementAmountOverLimit:
            payTaipeiProfitSharingConfig.allowDisbursementAmountOverLimit,
          defaultConfig: {
            type: payTaipeiProfitSharingConfig.defaultConfig
              .type as DefaultConfigType,
            value: payTaipeiProfitSharingConfig.defaultConfig.value,
          },
          bankDiscountConfig: {
            type: payTaipeiProfitSharingConfig.bankDiscountConfig
              .type as BankDiscountConfigType,
            value: payTaipeiProfitSharingConfig.bankDiscountConfig.value,
          },
          pbaReferralConfig: {
            type: payTaipeiProfitSharingConfig.pbaReferralConfig
              .type as PbaReferralConfigType,
            value: payTaipeiProfitSharingConfig.pbaReferralConfig.value,
          },
        },
      },
      existingAttachments: parkingContract.attachments,
    };
  };

  const parkingLotColumns: DataTableColumn<BriefParkingLot>[] = [
    {
      title: '場站名稱',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'name',
    },
  ];

  // callback functions
  function onDownloadAttachment(attachment: Attachment): void {
    if (isDownloadParkingContractAttachmentPending) return;

    const request = {
      partnerId: partnerId,
      contractId: parkingContract.id,
      attachmentId: attachment.id,
    };

    downloadParkingContractAttachment(request, {
      onSuccess: (downloadUrl) => {
        executeDownloadAttachment(downloadUrl, attachment.displayName);
        notifySuccess('下載檔案成功');
      },
    });
  }
  function onSubmit(values: MutateParkingContractFormValues): void {
    if (isUpdateContractPending) return;

    /* ===== 建構請求中共用部分 ===== */
    const baseRequest: BaseUpdateParkingContractRequest = {
      id: parkingContract.id,
      partnerId: partnerId,
      name: values.name,
      paperContractStartAt: values.paperContractStartAt,
      paperContractEndAt: values.paperContractEndAt,
      disbursementFeeProcessingMethod: values.disbursementFeeProcessingMethod,
      refundFeeProcessingMethod: values.refundFeeProcessingMethod,
      billingCycle: values.billingCycle,

      /* ===== 各交易場景 Profit Sharing 配置 ===== */
      ...{
        alprProfitSharingEnabled:
          values.parkingLotFunctionTypes.includes('ALPR'),
        alprAllowDisbursementAmountOverLimit:
          values.profitSharingConfigs.ALPR.allowDisbursementAmountOverLimit,
        alprDefaultProfitSharingType:
          values.profitSharingConfigs.ALPR.defaultConfig.type,
        alprDefaultProfitSharingValue:
          values.profitSharingConfigs.ALPR.defaultConfig.value,
        alprBankProfitSharingType:
          values.profitSharingConfigs.ALPR.bankDiscountConfig.type,
        alprBankProfitSharingValue:
          values.profitSharingConfigs.ALPR.bankDiscountConfig.value,
        alprPbaProfitSharingType:
          values.profitSharingConfigs.ALPR.pbaReferralConfig.type,
        alprPbaProfitSharingValue:
          values.profitSharingConfigs.ALPR.pbaReferralConfig.value,
      },
      ...{
        kioskProfitSharingEnabled:
          values.parkingLotFunctionTypes.includes('KIOSK'),
        kioskAllowDisbursementAmountOverLimit:
          values.profitSharingConfigs.KIOSK.allowDisbursementAmountOverLimit,
        kioskDefaultProfitSharingType:
          values.profitSharingConfigs.KIOSK.defaultConfig.type,
        kioskDefaultProfitSharingValue:
          values.profitSharingConfigs.KIOSK.defaultConfig.value,
        kioskBankProfitSharingType:
          values.profitSharingConfigs.KIOSK.bankDiscountConfig.type,
        kioskBankProfitSharingValue:
          values.profitSharingConfigs.KIOSK.bankDiscountConfig.value,
        kioskPbaProfitSharingType:
          values.profitSharingConfigs.KIOSK.pbaReferralConfig.type,
        kioskPbaProfitSharingValue:
          values.profitSharingConfigs.KIOSK.pbaReferralConfig.value,
      },
      ...{
        payTaipeiProfitSharingEnabled:
          values.parkingLotFunctionTypes.includes('PAY_TAIPEI'),
        payTaipeiAllowDisbursementAmountOverLimit:
          values.profitSharingConfigs.PAY_TAIPEI
            .allowDisbursementAmountOverLimit,
        payTaipeiDefaultProfitSharingType:
          values.profitSharingConfigs.PAY_TAIPEI.defaultConfig.type,
        payTaipeiDefaultProfitSharingValue:
          values.profitSharingConfigs.PAY_TAIPEI.defaultConfig.value,
        payTaipeiBankProfitSharingType:
          values.profitSharingConfigs.PAY_TAIPEI.bankDiscountConfig.type,
        payTaipeiBankProfitSharingValue:
          values.profitSharingConfigs.PAY_TAIPEI.bankDiscountConfig.value,
        payTaipeiPbaProfitSharingType:
          values.profitSharingConfigs.PAY_TAIPEI.pbaReferralConfig.type,
        payTaipeiPbaProfitSharingValue:
          values.profitSharingConfigs.PAY_TAIPEI.pbaReferralConfig.value,
      },
    };

    /* ===== 依據合約類型決定建構策略 ===== */
    const updateRequest:
      | UpdateDefaultParkingContractRequest
      | UpdateSpecifyParkingContractRequest =
      values.profileType === 'DEFAULT'
        ? ({
            ...baseRequest,
            profileType: 'DEFAULT',
            activeStartAt: values.activeStartAt,
            activeEndAt: values.activeEndAt,
          } as UpdateDefaultParkingContractRequest)
        : ({
            ...baseRequest,
            profileType: 'SPECIFY',
          } as UpdateSpecifyParkingContractRequest);

    const uploadAttachmentRequest: UploadParkingContractAttachmentRequest =
      values.newAttachments.map((attachment) => {
        return {
          partnerId: partnerId,
          contractId: parkingContract.id,
          file: attachment.file,
        };
      })[0];

    const deleteAttachmentRequest = values.deletedAttachmentIds.map((id) => {
      return {
        partnerId: partnerId,
        contractId: parkingContract.id,
        attachmentId: id,
      };
    })[0];

    updateContract(
      [updateRequest, uploadAttachmentRequest, deleteAttachmentRequest],
      {
        onSuccess: () => {
          notifySuccess('編輯停車場合約成功');
          queryClient.invalidateQueries({
            queryKey: [
              'partner',
              partnerId,
              'parkingContract',
              parkingContract.id,
            ],
          });
          setWarningClosed(false);
          setEditMode(false);
        },
      },
    );
  }
  function onDelete(): void {
    const request = {
      partnerId: partnerId,
      id: parkingContract.id,
    };

    const modalId = modals.openContextModal({
      modal: 'confirmModal',
      title: <Text fw={700}>刪除合約</Text>,
      closeOnEscape: true,
      closeOnClickOutside: true,
      centered: true,
      innerProps: {
        modalBody: `是否確認刪除此合約？`,
        loading: false,
        onConfirm: () => {
          modals.updateContextModal({
            modalId,
            innerProps: {
              loading: true,
            },
          });

          deleteContract(request, {
            onSuccess: () => {
              closeAllModals();
              // 轉導至 detailPage
              navigate(`/partners/${partnerId}`, {
                state: {
                  deleted: true,
                  message: `已成功刪除「${parkingContract.name}」停車場合約`,
                },
              });
            },
            onSettled: () => {
              modals.updateContextModal({
                modalId,
                innerProps: {
                  loading: false,
                },
              });
            },
          });
        },
      },
    });
  }
  function onPublish(): void {
    const request = {
      partnerId: partnerId,
      id: parkingContract.id,
    };

    const modalId = modals.openContextModal({
      modal: 'confirmModal',
      title: <Text fw={700}>發佈合約</Text>,
      closeOnEscape: true,
      closeOnClickOutside: true,
      centered: true,
      innerProps: {
        modalBody: `是否確認發佈此合約？`,
        loading: false,
        onConfirm: () => {
          modals.updateContextModal({
            modalId,
            innerProps: {
              loading: true,
            },
          });

          publishContract(request, {
            onSuccess: () => {
              closeAllModals();
              // 轉導至 detailPage
              navigate(`/partners/${partnerId}/parking-contracts/${id}`, {
                state: { published: true },
              });
            },
          });
        },
        onSettled: () => {
          modals.updateContextModal({
            modalId,
            innerProps: {
              loading: false,
            },
          });
        },
      },
    });
  }
  function onClickSetupSpecifyParkingLotsButton() {
    openSetupParkingLotsModal();
  }

  function handleSetupParkingLotsConfirm(formValues) {
    setupParingLots(
      { partnerId, id, ...formValues },
      {
        onSuccess: () => {
          closeSetupParkingLotsModal();
          notifySuccess('更新指定場站成功');
          queryClient.invalidateQueries({
            queryKey: [
              'partner',
              partnerId,
              'parkingContract',
              parkingContract.id,
            ],
          });
        },
        onError: (error) => {
          if (error.code === 'B0003') {
            notifyError(
              '新增指定場站失敗',
              '出現指定場站重複，請檢查其他停車場合約',
            );
          } else {
            notifyError('新增指定場站失敗', error.message);
          }
        },
      },
    );
  }

  return (
    <>
      {setupParkingLotsModalOpened && parkingContract && (
        <SetupSpecifyParkingLotsModal
          opened={setupParkingLotsModalOpened}
          onClose={closeSetupParkingLotsModal}
          initialValues={{
            activeStartAt: parkingContract.activeStartAt,
            activeEndAt: parkingContract.activeEndAt,
            parkingLotIds: parkingContract.specifyParkingLots.map(
              (pklot) => pklot.id,
            ),
          }}
          parkingLotData={briefParkingLots}
          isPending={isSetupParkingLotsPending}
          onConfirm={handleSetupParkingLotsConfirm}
          allowEditTime={true}
          allowBlankParkingLot={true}
        />
      )}
      {/* Main Content */}
      <PreviousPageButton
        href={`/partners/${partnerId}`}
        content={'返回業者詳情頁'}
      />

      {shouldDisplayCreatedNotice && (
        <Alert
          variant="light"
          color="green"
          mb={20}
          icon={<IconInfoCircle />}
          withCloseButton
          onClose={() => setCreatedNoticeClosed(true)}
          title="成功新增合約"
        />
      )}
      {shouldDisplayWarning && (
        <Alert
          variant="light"
          color="yellow"
          mb={20}
          icon={<IconInfoCircle />}
          withCloseButton
          onClose={() => setWarningClosed(true)}
          title="尚有資料未填寫完成，完成後才可發佈合約"
        />
      )}

      <Box mb={70}>
        <Stack gap="lg">
          {isEditMode ? (
            <Paper radius="lg" p="xl" shadow="xs">
              <Group justify="space-between">
                <Title order={3}>{`${partner.name} | 停車場合約`}</Title>
                <Group>
                  <Button
                    variant="outline"
                    onClick={() => setEditMode(false)}
                    fw={400}
                  >
                    取消
                  </Button>
                  <Button
                    type="submit"
                    form="mutateParkingContractForm"
                    loading={isUpdateContractPending}
                    fw={400}
                  >
                    儲存
                  </Button>
                </Group>
              </Group>

              <MutateParkingContractForm
                initialValues={initialFormValues()}
                onSubmit={onSubmit}
              />
            </Paper>
          ) : (
            <>
              <Paper radius="lg" p="xl" shadow="xs">
                <Group justify="space-between">
                  <Title order={3}>{`${partner.name} | 停車場合約`}</Title>
                  <Button
                    leftSection={<IconEdit size={20} />}
                    variant="outline"
                    onClick={() => setEditMode(true)}
                    fw={400}
                  >
                    編輯
                  </Button>
                </Group>

                <Text size="sm" fw={500} c="gray" mt={40}>
                  基本設定
                </Text>
                <Divider mt={10} mb={20} />
                <SimpleGrid cols={2} spacing="xl" verticalSpacing="xl" mb={60}>
                  <Input.Wrapper withAsterisk label="合約名稱">
                    <Text>{parkingContract.name}</Text>
                  </Input.Wrapper>
                  <Input.Wrapper label="合約套用方式">
                    <Text>
                      {PROFILE_TYPE_LABEL[parkingContract.profileType]}
                    </Text>
                  </Input.Wrapper>
                  <Input.Wrapper withAsterisk label="紙本合約生效起迄日">
                    <Group>
                      <Text>{`${formatDate(parkingContract.paperContractStartAt)}`}</Text>
                      <IconArrowRight size={20} />
                      <Text>{`${formatDate(parkingContract.paperContractEndAt)}`}</Text>
                    </Group>
                  </Input.Wrapper>
                  <Input.Wrapper withAsterisk label="合約生效起迄日">
                    <Group>
                      {parkingContract.activeStartAt ? (
                        <>
                          <Text>{`${formatDate(parkingContract.activeStartAt)}`}</Text>
                          <IconArrowRight size={20} />
                          <Text>{`${formatDate(parkingContract.activeEndAt)}`}</Text>
                        </>
                      ) : (
                        <Text>-</Text>
                      )}
                    </Group>
                  </Input.Wrapper>
                  <Input.Wrapper label="手續費扣除方式">
                    <Text>
                      {
                        PARKING_CONTRACT_DISBURSEMENT_FEE_PROCESSING_METHOD_LABEL[
                          parkingContract.disbursementFeeProcessingMethod
                        ]
                      }
                    </Text>
                  </Input.Wrapper>
                  <Input.Wrapper label="退款手續費處理方式">
                    <Text>
                      {
                        PARKING_CONTRACT_REFUND_FEE_PROCESSING_METHOD_LABEL[
                          parkingContract.refundFeeProcessingMethod
                        ]
                      }
                    </Text>
                  </Input.Wrapper>
                  <Input.Wrapper label="出帳週期">
                    <Text>
                      {
                        PARKING_CONTRACT_BILLING_CYCLE_LABEL[
                          parkingContract.billingCycle
                        ]
                      }
                    </Text>
                  </Input.Wrapper>
                  <Input.Wrapper label="交易情境">
                    <Text>
                      {[...parkingContract.parkingLotFunctionTypes]
                        .sort()
                        .map(
                          (type) =>
                            PARKING_CONTRACT_PARKING_LOT_FUNCTION_TYPE_LABEL[
                              type
                            ],
                        )
                        .join(' ｜ ')}
                    </Text>
                  </Input.Wrapper>
                </SimpleGrid>

                {['ALPR', 'KIOSK', 'PAY_TAIPEI'].map(
                  (parkingLotFunctionType: ParkingLotFunctionType) => {
                    if (
                      !parkingContract.parkingLotFunctionTypes.includes(
                        parkingLotFunctionType,
                      )
                    ) {
                      return null;
                    }
                    const config =
                      parkingContract.profitSharingConfigs[
                        parkingLotFunctionType
                      ];
                    return (
                      <>
                        <Text size="sm" fw={500} c="gray" mt={40}>
                          {`${PARKING_CONTRACT_PARKING_LOT_FUNCTION_TYPE_LABEL[parkingLotFunctionType]}分潤設定`}
                        </Text>
                        <Divider mt={10} mb={20} />
                        <SimpleGrid
                          cols={2}
                          spacing="xl"
                          verticalSpacing="xl"
                          mb={60}
                        >
                          <Input.Wrapper label="預設分潤條件">
                            <Text>{defaultProfitSharingText(config)}</Text>
                          </Input.Wrapper>
                          <Input.Wrapper label="有銀行折抵時分潤條件">
                            <Text>{bankDiscountProfitSharingText(config)}</Text>
                          </Input.Wrapper>
                          <Input.Wrapper label="有 PBA 導流時分潤條件">
                            <Text>{pbaReferralProfitSharingText(config)}</Text>
                          </Input.Wrapper>
                          <Input.Wrapper label="是否允許撥款金額超過原始請款金額">
                            <Text>
                              {config.allowDisbursementAmountOverLimit
                                ? '是'
                                : '否'}
                            </Text>
                          </Input.Wrapper>
                        </SimpleGrid>
                      </>
                    );
                  },
                )}

                <Text size="sm" fw={500} c="gray" mt={60} mb={20}>
                  合約上傳
                </Text>
                <Card
                  shadow="none"
                  style={{ border: '2px dashed var(--mantine-color-gray-2 )' }}
                >
                  <Text size="sm" fw={500} inline mb={10}>
                    已選擇檔案：
                  </Text>
                  {parkingContract.attachments.map((attachment) => {
                    return (
                      <Group justify="space-between">
                        <Text size="sm" fw={500}>
                          {attachment.displayName}
                        </Text>
                        <Button
                          variant="transparent"
                          onClick={() => onDownloadAttachment(attachment)}
                        >
                          下載檔案
                        </Button>
                      </Group>
                    );
                  })}
                </Card>
              </Paper>

              {parkingContract.profileType === 'SPECIFY' && (
                <Paper radius="lg" p="xl" shadow="xs">
                  <Group justify="space-between">
                    <Title order={3}>合約指定場站</Title>
                    <Button
                      leftSection={<IconEdit size={20} />}
                      variant="outline"
                      onClick={onClickSetupSpecifyParkingLotsButton}
                      disabled={parkingContract.profileType !== 'SPECIFY'}
                      fw={400}
                    >
                      編輯
                    </Button>
                  </Group>
                  <DataTable
                    idAccessor="id"
                    highlightOnHover
                    minHeight={150}
                    columns={parkingLotColumns}
                    records={parkingContract.specifyParkingLots}
                    verticalSpacing="sm"
                    noRecordsText="目前還沒有任何指定場站，點擊「編輯」開始建立！"
                    fetching={isBriefParkingLotsLoading}
                  />
                </Paper>
              )}
            </>
          )}
        </Stack>
      </Box>

      {/* Footer */}
      <Box
        pos="fixed"
        bottom={0}
        left={0}
        right={0}
        h={70}
        p="md"
        bg="var(--mantine-color-white)"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          zIndex: 100,
        }}
      >
        <Group justify="flex-end">
          <Button
            onClick={onDelete}
            loading={false}
            disabled={isEditMode}
            fw={400}
            color="red"
            leftSection={<IconTrash />}
          >
            刪除
          </Button>
          <Button
            onClick={onPublish}
            loading={false}
            disabled={!isPublishable}
            fw={400}
            leftSection={<IconStackPop />}
          >
            發佈
          </Button>
        </Group>
      </Box>
    </>
  );
}
